import api from '../../utils/api'
import { useState, useEffect } from 'react'
import Select from 'react-select'
import { FiCalendar, FiShoppingCart, FiRefreshCw, FiSend, FiFilePlus, FiFileText } from 'react-icons/fi'

import Button from '../../shared/components/Button/Button'
import Modal from '../../shared/modal/Modal'
import SelectStyles from '../../shared/css/SelectStyle'
import NewPurchaseOrder from './PurchaseOrder/NewPurchaseOrder'
import PurchaseOrderList from './PurchaseOrder/PurchaseOrderList'
import ProductionReportIcon from '../../shared/components/ProductionReportIcon/ProductionReportIcon'

/* Hooks */
import useMyPermission from '../../hooks/useMyPermission'
import useFlashMessage from '../../hooks/useFlashMessage'
import TimestampToString from '../../shared/functions/TimestampToString'
import buildPurchaseSummary from '../../shared/functions/buildPurchaseSummary'
import { PRODUCTION_SECTORS } from '../../shared/constants/productionSectors'

import './Purchase.css'

// Vocabulário de sub-status dentro do setor Compras — precisa bater com o que
// BudgetController.updatePurchaseStatus aceita (é uma string livre no backend,
// mas o app inteiro só deve usar estes 4 valores).
const PURCHASE_STATUSES = [
    { value: 'Aguardando Compra', label: 'Aguardando Compra' },
    { value: 'Pedido de Compra Realizado', label: 'Pedido de Compra Realizado — Aguardando Chegada' },
    { value: 'Separado do Estoque', label: 'Separado do Estoque' },
    { value: 'Pronto para Produção', label: 'Pronto para Produção' },
]

function statusBadgeClass(status) {
    switch (status) {
        case 'Aguardando Compra': return 'purchase-status-pendente'
        case 'Pedido de Compra Realizado': return 'purchase-status-aguardando'
        case 'Separado do Estoque': return 'purchase-status-separado'
        case 'Pronto para Produção': return 'purchase-status-pronto'
        default: return ''
    }
}

// Mesmo helper de frontend/src/pages/production/Production.js e
// frontend/src/pages/order/Report/OrderReport.js — resume o item num texto curto.
function descriptionText(item) {
    const parts = []
    if (item.category) parts.push(item.category.label || item.category.name)
    if (item.cloth) parts.push(item.cloth.label || item.cloth.name)
    if (item.sex) parts.push(item.sex.label)
    return parts.filter(Boolean).join(' · ')
}

function itemLabel(item) {
    if (!item) return 'Item'
    return item.simpleProduct ? item.description : (descriptionText(item) || item.description || 'Item')
}

function Purchase() {
    const [token] = useState(localStorage.getItem('token') || '')
    const { setFlashMessage } = useFlashMessage()
    const [orders, setOrders] = useState([])
    const [productionOrders, setProductionOrders] = useState([])
    const [sentPackages, setSentPackages] = useState([])
    const [load, setLoad] = useState(false)
    const { timeStampToDDMMAAAA } = TimestampToString()

    /* ---------------------- Permission ------------------------- */
    const [myPermission, setMyPermission] = useState({ group: '' })
    const { checkPermission } = useMyPermission()
    const myPermissionView = checkPermission(myPermission, 'purchase_view')
    const myPermissionEdit = checkPermission(myPermission, 'purchase_edit')
    const myPermissionProviderCreate = checkPermission(myPermission, 'provider_create')

    const [statusModalItem, setStatusModalItem] = useState(null)
    const [statusSelect, setStatusSelect] = useState(null)

    const [sendModalItem, setSendModalItem] = useState(null)
    const [sendSelect, setSendSelect] = useState(null)

    const [newPOItem, setNewPOItem] = useState(null)
    const [poListItem, setPoListItem] = useState(null)

    const getAll = () => {
        api.get('/order/purchase/getall', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
            .then((response) => {
                setOrders(response.data.order || [])
                setProductionOrders(response.data.productionOrders || [])
                setSentPackages(response.data.sentPackages || [])
                setMyPermission(response.data.permission)
                setLoad(true)
            })
    }

    useEffect(() => {
        getAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    function openStatusModal(item) {
        setStatusModalItem(item)
        setStatusSelect(PURCHASE_STATUSES.find((s) => s.value === item.status) || null)
    }

    async function submitStatus() {
        if (!statusSelect) return
        let msgType = 'success'
        const data = await api.patch(`/order/purchase/status/${statusModalItem._id}`, { status: statusSelect.value }, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            setStatusModalItem(null)
            getAll()
        }
    }

    function openSendModal(productionOrderId, orderCode, item) {
        setSendModalItem({ productionOrderId, orderCode, label: itemLabel(item) })
        setSendSelect(null)
    }

    async function submitSend() {
        if (!sendSelect) return
        let msgType = 'success'
        const data = await api.patch(`/order/purchase/sendItem/${sendModalItem.productionOrderId}`, { toSector: sendSelect.value }, {
            headers: { Authorization: `Bearer ${JSON.parse(token)}` },
        }).then((response) => response.data).catch((err) => { msgType = 'error'; return err.response.data })

        setFlashMessage(data.message, msgType)
        if (msgType !== 'error') {
            setSendModalItem(null)
            getAll()
        }
    }

    if (!load) {
        return <div className="pg-loading">Carregando compras...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Compras</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-right">
                            <span className="pg-count">
                                {orders.length} {orders.length === 1 ? 'pedido pendente' : 'pedidos pendentes'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="pg-list">
                {orders.length > 0 ? (
                    orders.map((item) => {
                        const summary = buildPurchaseSummary(item)
                        const ready = item.status === 'Pronto para Produção'
                        const itemOPs = (item.orderItemArray || []).map((orderItem, itemIndex) => ({
                            orderItem,
                            itemIndex,
                            op: productionOrders.find((po) => po.order === item._id && po.itemIndex === itemIndex),
                        })).filter((row) => row.op)
                        return (
                            <div key={item._id} className="pg-item">
                                <div className="pg-item-main">
                                    <div className="purchase-row-header">
                                        <span className="purchase-row-code">#{item.code}</span>
                                        <span className="purchase-row-name">{item.client?.name}</span>
                                        {item.orderEntryDate && (
                                            <span className="purchase-row-date"><FiCalendar size={11} />{timeStampToDDMMAAAA(item.orderEntryDate)}</span>
                                        )}
                                    </div>

                                    <div className="purchase-summary">
                                        {summary.length > 0 ? (
                                            summary.map((g, i) => (
                                                <div key={i} className="purchase-summary-item">
                                                    {g.type === 'aviamento' ? (
                                                        <span className="purchase-summary-cloth">{g.aviamentoName} <span className="purchase-summary-color">— Aviamento</span></span>
                                                    ) : (
                                                        <span className="purchase-summary-cloth">{g.clothName} <span className="purchase-summary-color">— {g.colorLabel}</span></span>
                                                    )}
                                                    {g.providerName && <span className="purchase-summary-provider">{g.providerName}</span>}
                                                    <span className="purchase-summary-qty">{g.total.toFixed(2)} {g.unitLabel}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="purchase-summary-empty">Nenhum item com consumo de tecido neste pedido.</p>
                                        )}
                                    </div>

                                    <div className="purchase-items">
                                        {itemOPs.map(({ orderItem, itemIndex, op }) => {
                                            const sentPkg = sentPackages.find((pkg) => pkg.productionOrder === op._id)
                                            return (
                                                <div key={itemIndex} className="purchase-item-row">
                                                    <span className="purchase-item-row-label">{itemLabel(orderItem)}</span>
                                                    {sentPkg ? (
                                                        <span className="purchase-item-row-sent">Enviado para {sentPkg.toSector}</span>
                                                    ) : myPermissionEdit && (
                                                        <FiSend
                                                            className={`purchase-icon-row-send${ready ? '' : ' purchase-icon-row-send-disabled'}`}
                                                            title={ready ? 'Enviar para Setor' : 'Marque "Pronto para Produção" antes de enviar'}
                                                            onClick={() => ready && openSendModal(op._id, item.code, orderItem)}
                                                        />
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>

                                <div className="purchase-row-status-col">
                                    <span className="purchase-row-status-caption">Status</span>
                                    <span className={`purchase-status-badge ${statusBadgeClass(item.status)}`}>{item.status}</span>
                                </div>

                                <div className="shd-row-box-item-action">
                                    <ProductionReportIcon title="Ver Dados do Pedido" onClick={() => window.open(`/order/report/${item._id}`, '_blank')} />
                                    <FiFileText className="shd-icon-row-edit" title="Ver Pedidos de Compra" onClick={() => setPoListItem(item)} />
                                    {myPermissionEdit && (
                                        <>
                                            <FiFilePlus className="shd-icon-row-edit" title="Novo Pedido de Compra" onClick={() => setNewPOItem(item)} />
                                            <FiRefreshCw className="shd-icon-row-edit" title="Atualizar Status" onClick={() => openStatusModal(item)} />
                                        </>
                                    )}
                                </div>
                            </div>
                        )
                    })
                ) : (
                    <div className="pg-empty">
                        <FiShoppingCart size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhuma compra pendente</p>
                        <p className="pg-empty-subtitle">Pedidos aparecem aqui assim que gerados a partir de um orçamento aprovado.</p>
                    </div>
                )}
            </div>

            <Modal
                modalStatus={!!statusModalItem}
                changeStatus={() => setStatusModalItem(null)}
                w={40}
                content={
                    statusModalItem && (
                        <div className="purchase-modal">
                            <p className="purchase-modal-title">Atualizar Status — #{statusModalItem.code}</p>
                            <span className="purchase-modal-title-accent" />
                            <Select
                                options={PURCHASE_STATUSES}
                                styles={SelectStyles}
                                value={statusSelect}
                                onChange={setStatusSelect}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                placeholder="Selecione o status..."
                            />
                            <div className="purchase-modal-actions">
                                <Button variant="cancel" onClick={() => setStatusModalItem(null)}>Cancelar</Button>
                                <Button variant="save" onClick={submitStatus}>Salvar</Button>
                            </div>
                        </div>
                    )
                }
            />

            <Modal
                modalStatus={!!sendModalItem}
                changeStatus={() => setSendModalItem(null)}
                w={40}
                content={
                    sendModalItem && (
                        <div className="purchase-modal">
                            <p className="purchase-modal-title">Enviar para Setor — #{sendModalItem.orderCode} · {sendModalItem.label}</p>
                            <span className="purchase-modal-title-accent" />
                            <Select
                                options={PRODUCTION_SECTORS}
                                styles={SelectStyles}
                                value={sendSelect}
                                onChange={setSendSelect}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                placeholder="Selecione o setor de destino..."
                            />
                            <div className="purchase-modal-actions">
                                <Button variant="cancel" onClick={() => setSendModalItem(null)}>Cancelar</Button>
                                <Button variant="finish" onClick={submitSend}>Enviar</Button>
                            </div>
                        </div>
                    )
                }
            />

            <Modal
                modalStatus={!!newPOItem}
                changeStatus={() => setNewPOItem(null)}
                w={70}
                content={
                    newPOItem && (
                        <NewPurchaseOrder
                            order={newPOItem}
                            btCancelAction={() => setNewPOItem(null)}
                            handleOnSuccess={() => { setNewPOItem(null); getAll() }}
                            myPermissionProviderCreate={myPermissionProviderCreate}
                        />
                    )
                }
            />

            <Modal
                modalStatus={!!poListItem}
                changeStatus={() => setPoListItem(null)}
                w={60}
                content={
                    poListItem && (
                        <PurchaseOrderList
                            order={poListItem}
                            myPermissionEdit={myPermissionEdit}
                            myPermissionProviderCreate={myPermissionProviderCreate}
                            handleOnChange={getAll}
                        />
                    )
                }
            />

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar Compras</p>
        </div>
    )
}

export default Purchase
